import { FC, FormEvent, PointerEvent, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { Alert, Box, Button, Checkbox, FormControlLabel, Paper, Stack, TextField, Typography } from '@mui/material'
import Logo from 'views/components/UI/Logo'
import TrainingCalendar from '../super-admin/trainings/calendar'

interface LegalDocument {
  content: string
  version: string
}

interface LinkInfo {
  company_name: string
  legal_document?: LegalDocument
  process_name?: string
  process_type?: 'open' | 'in_house'
  space_name?: string
  starts_at?: string
  ends_at?: string
  hours?: number
  reaccept?: boolean
  options?: Array<{ id: number; name: string; type: 'open' | 'in_house'; spaces: Array<{ id: number; kind: 'betesa' | 'skills_3d' | 'ideal_profiles'; starts_at: string }> }>
  selected_process_id?: number | null
}

interface PersonalizedDocument {
  document_text: string
  document_hash: string
  template_version: string
  privacy_text: string
  privacy_hash: string
}

const api = process.env.REACT_APP_MAIN_API
const personalEmailDomains: Set<string> = new Set(['gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.es', 'outlook.com', 'outlook.es', 'live.com', 'live.es', 'msn.com', 'yahoo.com', 'yahoo.es', 'icloud.com', 'me.com', 'aol.com', 'proton.me', 'protonmail.com'])

const TrainingRegistration: FC<{ reacceptMode?: boolean }> = ({ reacceptMode = false }) => {
  const { token } = useParams<{ token: string }>()
  const endpoint = `${api}/trainings/${reacceptMode ? 'reaccept' : 'public'}/${token}`
  const canvas = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const hasStroke = useRef(false)
  const [link, setLink] = useState<LinkInfo | null>(null)
  const [selectedProcessId, setSelectedProcessId] = useState<number | null>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [phone, setPhone] = useState('')
  const [accepted, setAccepted] = useState(false)
  const [ownershipAccepted, setOwnershipAccepted] = useState(false)
  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [document, setDocument] = useState<PersonalizedDocument | null>(null)
  const [verificationId, setVerificationId] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [signaturePng, setSignaturePng] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [done, setDone] = useState(false)
  const [confirmationEmailFailed, setConfirmationEmailFailed] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const rootElement = window.document.getElementById('root')
    const bodyStyle = window.document.body.style
    const rootStyle = rootElement?.style
    const originalBodyHeight = bodyStyle.height
    const originalBodyMinHeight = bodyStyle.minHeight
    const originalBodyOverflowX = bodyStyle.overflowX
    const originalBodyOverflowY = bodyStyle.overflowY
    const originalRootHeight = rootStyle?.height || ''
    const originalRootMinHeight = rootStyle?.minHeight || ''
    const originalRootOverflow = rootStyle?.overflow || ''

    bodyStyle.height = 'auto'
    bodyStyle.minHeight = '100vh'
    bodyStyle.overflowX = 'hidden'
    bodyStyle.overflowY = 'auto'
    if (rootStyle) {
      rootStyle.height = 'auto'
      rootStyle.minHeight = '100vh'
      rootStyle.overflow = 'visible'
    }

    return () => {
      bodyStyle.height = originalBodyHeight
      bodyStyle.minHeight = originalBodyMinHeight
      bodyStyle.overflowX = originalBodyOverflowX
      bodyStyle.overflowY = originalBodyOverflowY
      if (rootStyle) {
        rootStyle.height = originalRootHeight
        rootStyle.minHeight = originalRootMinHeight
        rootStyle.overflow = originalRootOverflow
      }
    }
  }, [])

  useEffect(() => {
    axios.get(endpoint)
      .then((response) => { const info = response.data.data as LinkInfo; setLink(info); setSelectedProcessId(info.selected_process_id || null) })
      .catch((requestError) => setError(requestError.response?.data?.message || 'Este enlace no está disponible.'))
      .finally(() => setLoading(false))
  }, [endpoint])

  const clearDocument = () => {
    setDocument(null)
    setVerificationId('')
    setVerificationCode('')
    setOwnershipAccepted(false)
    setPrivacyAccepted(false)
    setSignaturePng('')
    hasStroke.current = false
  }
  const preview = async () => {
    setSending(true)
    setError('')
    try {
      const response = await axios.post(`${endpoint}/preview`, { name: name.trim(), email: email.trim(), ...(!reacceptMode && selectedProcessId ? { process_id: selectedProcessId } : {}) })
      setDocument(response.data.data as PersonalizedDocument)
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudo preparar el documento.')
    } finally { setSending(false) }
  }
  const sendCode = async () => {
    setSending(true)
    setError('')
    try {
      const response = await axios.post(`${endpoint}/verification`, { email: email.trim() })
      setVerificationId(response.data.data.verification_id)
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudo enviar el código.')
    } finally { setSending(false) }
  }
  const downloadDocument = () => {
    if (!document) return
    const file = new Blob([`${document.document_text}\n\nTRATAMIENTO DE DATOS\n\n${document.privacy_text}`], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(file)
    const anchor = window.document.createElement('a')
    anchor.href = url
    anchor.download = 'documento-entrenamiento-bluesite.txt'
    anchor.click()
    URL.revokeObjectURL(url)
  }
  const pointerPosition = (event: PointerEvent<HTMLCanvasElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect()
    return { x: (event.clientX - bounds.left) * event.currentTarget.width / bounds.width, y: (event.clientY - bounds.top) * event.currentTarget.height / bounds.height }
  }
  const startSignature = (event: PointerEvent<HTMLCanvasElement>) => {
    const context = canvas.current?.getContext('2d')
    if (!context) return
    event.currentTarget.setPointerCapture(event.pointerId)
    const point = pointerPosition(event)
    context.strokeStyle = '#163247'
    context.lineWidth = 2.5
    context.lineCap = 'round'
    context.beginPath()
    context.moveTo(point.x, point.y)
    drawing.current = true
    hasStroke.current = false
  }
  const moveSignature = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    const context = canvas.current?.getContext('2d')
    if (!context) return
    const point = pointerPosition(event)
    context.lineTo(point.x, point.y)
    context.stroke()
    hasStroke.current = true
  }
  const stopSignature = () => {
    if (drawing.current && canvas.current) {
      drawing.current = false
      if (hasStroke.current) setSignaturePng(canvas.current.toDataURL('image/png'))
    }
  }
  const clearSignature = () => {
    canvas.current?.getContext('2d')?.clearRect(0, 0, 620, 150)
    setSignaturePng('')
    hasStroke.current = false
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!link || sending) return
    if (link.options?.length && !selectedProcessId) { setError('Selecciona una convocatoria completa para continuar.'); return }
    if (link.space_name && (!document || !verificationId || !verificationCode || !signaturePng || !ownershipAccepted || !privacyAccepted)) {
      setError('Completa la verificación, ambas aceptaciones y la firma.')
      return
    }
    if (!link.space_name && !accepted) return
    if (personalEmailDomains.has(email.trim().toLowerCase().split('@')[1] || '')) {
      setError('Introduce un correo electrónico corporativo. No se admiten cuentas personales.')
      return
    }
    setSending(true)
    setError('')
    try {
      const response = link.space_name
        ? await axios.post(link.reaccept ? endpoint : `${endpoint}/registrations`, {
          name, email, ...(!link.reaccept ? { job_title: jobTitle, phone } : {}), accept_legal: true,
          accept_ownership: ownershipAccepted, accept_privacy: privacyAccepted,
          signature_png: signaturePng, template_version: document?.template_version,
          document_hash: document?.document_hash, privacy_hash: document?.privacy_hash,
          verification_id: verificationId, verification_code: verificationCode,
          ...(selectedProcessId ? { process_id: selectedProcessId } : {}),
        })
        : await axios.post(`${endpoint}/registrations`, {
          name, email, job_title: jobTitle, phone, accept_legal: true,
          legal_document_version: link.legal_document?.version,
        })
      setConfirmationEmailFailed(!link.reaccept && response.data.data.confirmation_email_status !== 'sent')
      setDone(true)
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudo completar la inscripción.')
    } finally {
      setSending(false)
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#F7FAFD', px: { xs: 1.5, sm: 3 }, py: { xs: 3, sm: 6 } }}>
      <Paper sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2.5, sm: 4 }, border: '1px solid #D7E0EA', borderRadius: 3, boxShadow: '0 12px 36px rgba(22, 50, 71, 0.08)' }}>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
            <Box sx={{ flexShrink: 0, width: { xs: 160, sm: 175 } }}>
              <Logo width="100%" />
            </Box>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="overline" sx={{ color: '#2D749C', fontWeight: 700, letterSpacing: 1.2 }}>BLUESITE HR HUB · FORMACIÓN</Typography>
              <Typography variant="h4" sx={{ mt: 0.5, color: '#163247', fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>{link?.reaccept ? 'Nueva aceptación del entrenamiento' : 'Inscripción al entrenamiento'}</Typography>
              <Typography sx={{ mt: 1, color: '#5E7183' }}>Formulario oficial para registrar tu cupo de entrenamiento.</Typography>
            </Box>
          </Box>
          <Box sx={{ height: 1, backgroundColor: '#D7E0EA' }} />
        {loading && <Typography>Cargando cupo...</Typography>}
        {error && <Alert severity={error === 'Este cupo ya está ocupado.' ? 'info' : 'error'} sx={{ mb: 2 }}>{error}</Alert>}
        {done && <Alert severity="success">{link?.reaccept ? 'La nueva fecha y el documento han quedado aceptados.' : <Stack spacing={0.5}>
          <Typography>Hola {name.trim()}</Typography>
          <Typography>Inscripción realizada.</Typography>
          <Typography>Tu cupo ha sido registrado.</Typography>
          <Typography>Nuestro equipo está preparándolo todo.</Typography>
          {!confirmationEmailFailed && <Typography>En unos minutos te llegará el correo de confirmación de la inscripción.</Typography>}
          <Typography>Más adelante, te enviaremos las invitaciones con el link de conexión al entrenamiento.</Typography>
          <Typography>Equipo de Bluesite</Typography>
        </Stack>}</Alert>}
        {done && confirmationEmailFailed && <Alert severity="warning">Tu inscripción está registrada, pero no pudimos enviarte el correo de confirmación. Contacta con servicios@bluesitehr.com si lo necesitas.</Alert>}
        {link && !done && (
          <form onSubmit={submit}>
            <Stack spacing={2}>
              <Box sx={{ p: 2, borderRadius: 2, backgroundColor: '#E9EEF6', borderLeft: '4px solid #C6016B' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1 }}>
                  <Typography variant="overline" sx={{ color: '#5E7183', fontWeight: 700 }}>Empresa que te invita</Typography>
                  <Typography variant="h6" sx={{ color: '#163247' }}>{link.company_name}</Typography>
                </Box>
                {link.space_name ? <Typography variant="body2" sx={{ color: '#5E7183' }}>{selectedProcessId ? `${link.options?.find((item) => item.id === selectedProcessId)?.name || link.process_name} · Tres bloques, 16 horas en total` : link.process_name ? `${link.process_name} · ${link.space_name}` : 'Selecciona una convocatoria completa con sus tres fechas'}</Typography> : <Typography variant="body2" sx={{ color: '#5E7183' }}>Inscripción gestionada por Bluesite HR Hub</Typography>}
              </Box>
              {link.options?.length && !link.reaccept ? <Box><Typography variant="h6" sx={{ mb: 1 }}>Elige tu convocatoria completa</Typography><TrainingCalendar selectionMode selectedId={selectedProcessId} processes={link.options.map((item) => ({ ...item, status: 'enabled', company_name: null, spaces: item.spaces.map((space) => ({ ...space, ends_at: space.starts_at, hours: '16' })) }))} onOpen={(id) => { if (id !== selectedProcessId) { setSelectedProcessId(id); clearDocument(); setError('') } }} />{selectedProcessId && <Typography sx={{ mt: 1, color: '#2D749C' }}>Has elegido {link.options.find((item) => item.id === selectedProcessId)?.name}. Tu inscripción incluye las tres fechas de esta convocatoria.</Typography>}</Box> : null}
              {link.options?.length && link.reaccept && <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography variant="h6">Fechas de tu convocatoria</Typography>{(link.options.find((item) => item.id === selectedProcessId) || link.options[0]).spaces.map((space) => <Typography key={space.id} sx={{ color: '#5E7183' }}>{({ betesa: 'Betesa', skills_3d: '3D Skills', ideal_profiles: 'Perfiles ideales' } as Record<string, string>)[space.kind]}: {new Date(space.starts_at).toLocaleDateString('es-CO', { timeZone: 'America/Bogota', dateStyle: 'long' })}</Typography>)}<Typography sx={{ mt: 1, fontWeight: 700, color: '#2D749C' }}>Intensidad total: 16 horas</Typography></Paper>}
              <TextField label="Nombre completo" value={name} onChange={(event) => { setName(event.target.value); clearDocument() }} required inputProps={{ maxLength: 255 }} />
              <TextField label="Correo electrónico corporativo" type="email" value={email} onChange={(event) => { setEmail(event.target.value); clearDocument() }} required inputProps={{ maxLength: 255 }} error={personalEmailDomains.has(email.trim().toLowerCase().split('@')[1] || '')} helperText="No se admiten cuentas de correo personales como Gmail, Hotmail u Outlook." />
              {!link.reaccept && <><TextField label="Cargo" value={jobTitle} onChange={(event) => setJobTitle(event.target.value)} required inputProps={{ maxLength: 255 }} /><TextField label="Teléfono" value={phone} onChange={(event) => setPhone(event.target.value)} required inputProps={{ maxLength: 100 }} /></>}
              {link.space_name ? <>
                <Button variant="outlined" disabled={sending || !name.trim() || !email.trim() || Boolean(link.options?.length && !selectedProcessId)} onClick={preview} sx={{ whiteSpace: 'normal', textTransform: 'none', textAlign: 'center', py: 1 }}>Leer y aceptar condiciones de uso y protección de contenidos</Button>
                {document && <>
                  <Paper variant="outlined" sx={{ p: 2, maxHeight: 360, overflowY: 'auto', whiteSpace: 'pre-line', borderColor: '#D7E0EA' }}><Typography variant="subtitle1" fontWeight={700}>Condiciones de uso y protección de contenidos</Typography><Typography variant="body2" sx={{ lineHeight: 1.7 }}>{document.document_text}</Typography></Paper>
                  <Button sx={{ alignSelf: 'flex-start', textTransform: 'none' }} onClick={downloadDocument}>Descargar documento</Button>
                  <Paper variant="outlined" sx={{ p: 2, whiteSpace: 'pre-line', borderColor: '#D7E0EA' }}><Typography variant="subtitle1" fontWeight={700}>Tratamiento de datos personales</Typography><Typography variant="body2" sx={{ lineHeight: 1.7 }}>{document.privacy_text}</Typography></Paper>
                  <FormControlLabel control={<Checkbox checked={ownershipAccepted} onChange={(event) => setOwnershipAccepted(event.target.checked)} />} label="La aceptación electrónica expresa mi consentimiento sobre el contenido íntegro de este documento." />
                  <FormControlLabel control={<Checkbox checked={privacyAccepted} onChange={(event) => setPrivacyAccepted(event.target.checked)} />} label="Autorizo el tratamiento de mis datos para gestionar este entrenamiento." />
                  <Box><Typography variant="body2" fontWeight={700}>Dibuja tu firma</Typography><canvas ref={canvas} width={620} height={150} onPointerDown={startSignature} onPointerMove={moveSignature} onPointerUp={stopSignature} onPointerCancel={stopSignature} style={{ display: 'block', width: '100%', maxWidth: 620, height: 150, border: '1px solid #B9CCDC', borderRadius: 8, touchAction: 'none' }} /><Button size="small" onClick={clearSignature}>Borrar firma</Button></Box>
                  <Button variant="outlined" disabled={sending || Boolean(verificationId)} onClick={sendCode}>{verificationId ? 'Código enviado al correo' : 'Enviar código de verificación'}</Button>
                  {verificationId && <TextField label="Código de seis cifras" value={verificationCode} onChange={(event) => setVerificationCode(event.target.value)} required inputProps={{ maxLength: 6, inputMode: 'numeric' }} helperText="Caduca en 10 minutos." />}
                </>}
              </> : <><Paper variant="outlined" sx={{ p: 2, whiteSpace: 'pre-line', borderColor: '#D7E0EA', borderRadius: 2, backgroundColor: '#FCFDFE' }}><Typography variant="subtitle1" sx={{ color: '#163247', fontWeight: 700 }}>Aviso legal y tratamiento de datos</Typography><Typography variant="body2" sx={{ mt: 1, color: '#5E7183', lineHeight: 1.7 }}>{link.legal_document?.content}</Typography></Paper><FormControlLabel control={<Checkbox checked={accepted} onChange={(event) => setAccepted(event.target.checked)} />} label="He leído y acepto el aviso legal y el tratamiento de mis datos" /></>}
              <Button type="submit" variant="contained" disabled={sending || (link.space_name ? !document || !verificationId || !verificationCode || !signaturePng || !ownershipAccepted || !privacyAccepted : !accepted)} sx={{ minHeight: 48, fontWeight: 700, textTransform: 'none', background: '#397a9d', '&:hover': { background: '#286584' } }}>{sending ? 'Procesando...' : link.reaccept ? 'Aceptar nueva fecha' : 'Inscribirme'}</Button>
            </Stack>
          </form>
        )}
        </Stack>
      </Paper>
    </Box>
  )
}

export default TrainingRegistration
